import { useState, useEffect } from "react";
import { api } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { X, FlaskConical } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Slider } from "../ui/slider";

export default function CreateABTestModal({ onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [vessels, setVessels] = useState([]);
  const [selectedVessels, setSelectedVessels] = useState([]);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    test_type: "subject",
    variant_a_name: "Variant A",
    variant_a_subject: "",
    variant_a_content: "",
    variant_b_name: "Variant B",
    variant_b_subject: "",
    variant_b_content: "",
    traffic_split: 50,
    test_duration: 24
  });

  useEffect(() => {
    fetchVessels();
  }, []);

  const fetchVessels = async () => {
    try {
      const response = await api.get("/vessels?active_only=true");
      setVessels(response.data);
    } catch (error) {
      console.error("Failed to load vessels");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleVesselToggle = (vesselId) => {
    setSelectedVessels(prev => 
      prev.includes(vesselId)
        ? prev.filter(id => id !== vesselId)
        : [...prev, vesselId]
    );
  };

  const handleSelectAll = () => {
    setSelectedVessels(vessels.map(v => v.vessel_id));
  };

  const handleSelectNone = () => {
    setSelectedVessels([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.variant_a_subject || !formData.variant_b_subject) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (selectedVessels.length < 4) {
      toast.error("Please select at least 4 recipients for statistical significance");
      return;
    }

    setLoading(true);

    try {
      const data = {
        ...formData,
        vessel_ids: selectedVessels,
        traffic_split: formData.traffic_split,
        test_duration: formData.test_duration || null
      };

      await api.post("/ab-tests", data);
      toast.success("A/B test created successfully");
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create A/B test");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center modal-overlay p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto animate-fadeIn">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <FlaskConical className="h-6 w-6 text-primary" />
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Create A/B Test
            </h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Test Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Test Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Subject Line Test - Newsletter"
                  data-testid="abtest-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="test_type">Test Type</Label>
                <Select 
                  value={formData.test_type} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, test_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="subject">Subject Line</SelectItem>
                    <SelectItem value="content">Email Content</SelectItem>
                    <SelectItem value="send_time">Send Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={2}
                placeholder="Brief description of what you're testing"
              />
            </div>
          </div>

          {/* Test Configuration */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Test Configuration
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <Label>Traffic Split: {formData.traffic_split}% / {100 - formData.traffic_split}%</Label>
                <Slider
                  value={[formData.traffic_split]}
                  onValueChange={([v]) => setFormData(prev => ({ ...prev, traffic_split: v }))}
                  min={10}
                  max={90}
                  step={5}
                  className="py-4"
                />
                <p className="text-xs text-muted-foreground">
                  Variant A gets {formData.traffic_split}%, Variant B gets {100 - formData.traffic_split}%
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="test_duration">Test Duration (hours)</Label>
                <Input
                  id="test_duration"
                  name="test_duration"
                  type="number"
                  value={formData.test_duration}
                  onChange={handleChange}
                  min={1}
                  max={168}
                  placeholder="24"
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty for manual completion
                </p>
              </div>
            </div>
          </div>

          {/* Variants */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Test Variants
            </h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Variant A */}
              <div className="space-y-4 p-4 border border-primary/30 bg-primary/5 rounded-lg">
                <div className="space-y-2">
                  <Label htmlFor="variant_a_name">Variant A Name</Label>
                  <Input
                    id="variant_a_name"
                    name="variant_a_name"
                    value={formData.variant_a_name}
                    onChange={handleChange}
                    placeholder="Variant A"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="variant_a_subject">Subject Line *</Label>
                  <Input
                    id="variant_a_subject"
                    name="variant_a_subject"
                    value={formData.variant_a_subject}
                    onChange={handleChange}
                    required
                    placeholder="Don't miss our latest updates!"
                    data-testid="variant-a-subject"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="variant_a_content">Content</Label>
                  <Textarea
                    id="variant_a_content"
                    name="variant_a_content"
                    value={formData.variant_a_content}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Email content for Variant A..."
                  />
                </div>
              </div>

              {/* Variant B */}
              <div className="space-y-4 p-4 border border-chart-2/30 bg-chart-2/5 rounded-lg">
                <div className="space-y-2">
                  <Label htmlFor="variant_b_name">Variant B Name</Label>
                  <Input
                    id="variant_b_name"
                    name="variant_b_name"
                    value={formData.variant_b_name}
                    onChange={handleChange}
                    placeholder="Variant B"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="variant_b_subject">Subject Line *</Label>
                  <Input
                    id="variant_b_subject"
                    name="variant_b_subject"
                    value={formData.variant_b_subject}
                    onChange={handleChange}
                    required
                    placeholder="Important news for your vessel"
                    data-testid="variant-b-subject"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="variant_b_content">Content</Label>
                  <Textarea
                    id="variant_b_content"
                    name="variant_b_content"
                    value={formData.variant_b_content}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Email content for Variant B..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Recipients */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Recipients ({selectedVessels.length} selected)
            </h3>
            
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-muted-foreground">
                Select at least 4 recipients for statistical significance
              </span>
              <div className="flex gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={handleSelectAll}>
                  Select All
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={handleSelectNone}>
                  Select None
                </Button>
              </div>
            </div>
            
            <div className="max-h-48 overflow-y-auto border border-border rounded-lg">
              {vessels.map((vessel) => (
                <div
                  key={vessel.vessel_id}
                  className="flex items-center gap-3 p-3 hover:bg-secondary/30 border-b border-border last:border-0 cursor-pointer"
                  onClick={() => handleVesselToggle(vessel.vessel_id)}
                >
                  <Checkbox
                    checked={selectedVessels.includes(vessel.vessel_id)}
                    onCheckedChange={() => handleVesselToggle(vessel.vessel_id)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{vessel.name}</div>
                    <div className="text-sm text-muted-foreground truncate">{vessel.email}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="btn-primary" 
              disabled={loading}
              data-testid="create-abtest-submit"
            >
              {loading ? "Creating..." : "Create A/B Test"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
